
import { Body2Reg, Caption1Bold, Caption1Reg, Subtitle1 } from '../../../config/designSystem/typography';
import { COLORS } from '../../../config/designSystem/Colors';
import { ElementRef, forwardRef } from 'react';
import { Feather } from '@expo/vector-icons';
import { Image, StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { SPACING } from '../../../config/designSystem/Spacing';
import { observer } from 'mobx-react';
import { useStores } from '../../../hooks/useStores';

interface AppointmentCardProps {
  counsellorName: string;
  counsellorPicture?: string;
  locale: string;
  date: string;
  timeZone: string;
  appointmentModeLabelKey: string;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
}
// use forwardRef to be a child of expo-router/Link
// https://github.com/expo/router/issues/278#issuecomment-1656025761
const _AppointmentCard = forwardRef<ElementRef<typeof TouchableOpacity>, AppointmentCardProps>(({
  locale,
  date: dateString,
  timeZone,
  counsellorName,
  counsellorPicture,
  appointmentModeLabelKey,
  onPress,
  style
}, ref) => {
  const {language} = useStores();
  const labels= language?.translate.MOBILE?.ASSISTANCE?.APPOINTMENT;
  const date = dateString ? new Date(dateString) : undefined;
  const dateFormatted = date ? new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long'
  }).format(date) : undefined;
  const timeFormatted = date ? new Intl.DateTimeFormat(locale, {
    timeStyle: 'short',
  }).format(date) : undefined;
  const timeZoneShort = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: 'short'
  }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value;

  return (
    <View style={[styles.AppointmentCard, style]}>
      <TouchableOpacity
        style={styles.AppointmentCardTouchable}
        onPress={onPress}
        ref={ref}
      >
        <View style={styles.AppointmentCardDateTime}>
          <View style={styles.AppointmentCardDateTimeItem}>
            <Feather
              name="calendar"
              color={COLORS.content.accent}
              size={18}
            />
            <Caption1Bold color={COLORS.content.accent}>{dateFormatted}</Caption1Bold>
          </View>
          <View style={styles.AppointmentCardDateTimeItem}>
            <Feather
              name="clock"
              color={COLORS.content.accent}
              size={18}
            />
            <Caption1Bold color={COLORS.content.accent}>{timeFormatted}</Caption1Bold>
            <Caption1Reg color={COLORS.content.accent}>({timeZoneShort || timeZone})</Caption1Reg>
          </View>
        </View>
        <View style={styles.AppointmentCardPersona}>
          <View style={styles.Avatar}>
            {counsellorPicture !== undefined ?
              <Image
                width={40}
                height={40}
                resizeMode='contain'
                style={styles.Image}
                source={{uri: counsellorPicture}}
              />
              :
              <Feather
                name="user"
                size={26}
                color={COLORS.content.accent}
                style={{alignSelf: 'center'}}
              />
            }
          </View>
          <View style={styles.AppointmentCardPersonaContent}>
            <Subtitle1>
              {counsellorName}
            </Subtitle1>
            <Body2Reg>
              {labels?.MODE[appointmentModeLabelKey] || appointmentModeLabelKey}
            </Body2Reg>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
});

const styles = StyleSheet.create({
  AppointmentCard: {
    borderRadius: SPACING['sp-12'],
    backgroundColor: COLORS.surface.contrast,
    shadowColor: COLORS.brand[500],
    shadowOffset: {height: 4, width: 0},
    shadowOpacity: 0.1
  },
  AppointmentCardTouchable: {
    display: 'flex',
    padding: SPACING['sp-16'],
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: SPACING['sp-16'],
  },
  AppointmentCardDateTime: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING['sp-12'],
  },
  AppointmentCardDateTimeItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING['sp-8'],
  },
  AppointmentCardPersona: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: SPACING['sp-16'],
    alignSelf: 'stretch',
  },
  AppointmentCardPersonaContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1
  },
  Avatar: {
    width: SPACING['sp-40'],
    height: SPACING['sp-40'],
    backgroundColor: COLORS.surface.accent,
    justifyContent: 'center',
    borderRadius: 99,
  },
  Image: {
    borderRadius: 99,
    alignSelf: 'center',
    height: '100%',
    width: '100%'
  }
});
export const AppointmentCard = observer(_AppointmentCard);
